import { useState } from "react";

import Button from "@/components/button/button";
import Dialog from "@/components/dialog/dialog";
import DialogHeader from "@/components/dialog/dailog-header";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import Icon from "@/components/icon/icon";
import Stack from "@/components/stack/stack";
import { SortableItem, SortableList } from "@/features/admin/components/sortable-list";
import TimeSeriesConfigCard from "@/features/widget-forms/forms/weather-model-map/components/time-series-config-card";
import styles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";
import { useTranslation } from "@/lib/i18n";

import sectionStyles from "./observations-map-display-group-section.module.css";
import { useObservationsMapForm } from "./use-observations-map-form";
import { useObservationsMapTimeSeriesConfigurator } from "./observations-map-time-series-configurator-context";

function RemoveConfigConfirmDialog({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Content className={dialogVariants.default}>
        <DialogHeader.Root>
          <DialogHeader.Title>
            {t("admin.widgets.fields.observations_map.time_series.remove_config_confirm")}
          </DialogHeader.Title>
        </DialogHeader.Root>
        <Stack justifyContent="flex-end" gap={8} flexDirection="row">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("general.cancel")}
          </Button>
          <Button type="button" variant="solid" onClick={onConfirm}>
            {t(
              "admin.widgets.fields.observations_map.time_series.remove_config_confirm_button",
            )}
          </Button>
        </Stack>
      </Dialog.Content>
    </Dialog>
  );
}

type ObservationsMapTimeSeriesSectionProps = {
  showSectionTitle?: boolean;
};

/**
 * Time-series list for obs-map. Opening/editing an entry is delegated to the
 * editor form via {@link useObservationsMapTimeSeriesConfigurator} so the
 * configurator can be presented in different locations (see dev variants).
 */
export function ObservationsMapTimeSeriesSection({
  showSectionTitle = false,
}: ObservationsMapTimeSeriesSectionProps) {
  const { t } = useTranslation();
  const {
    parameters,
    timeSeriesConfigurations,
    removeTimeSeriesConfig,
    reorderTimeSeriesConfigs,
  } = useObservationsMapForm();
  const { openFor } = useObservationsMapTimeSeriesConfigurator();

  const [removeConfirmIndex, setRemoveConfirmIndex] = useState<number | null>(null);

  return (
    <div className={styles.settingsPanel}>
      {showSectionTitle && (
        <div className={sectionStyles.sectionTitleBlock}>
          <h3 className={sectionStyles.sectionTitle}>
            {t("admin.widgets.fields.observations_map.time_series.section_title")}
          </h3>
          <p className={sectionStyles.sectionSubtitle}>
            {t("admin.widgets.fields.observations_map.time_series.section_subtitle")}
          </p>
        </div>
      )}
      <div className={styles.sectionHeader}>
        <Button type="button" variant="solid" onClick={() => openFor(null)}>
          <Stack gap={8} alignItems="center">
            <Icon name="plus" size={16} />
            <span>{t("admin.widgets.fields.observations_map.time_series.add")}</span>
          </Stack>
        </Button>
      </div>
      <div className={styles.settingsPanelCards}>
        {timeSeriesConfigurations.length > 0 ? (
          <SortableList
            onReorder={reorderTimeSeriesConfigs}
            className={styles.cardGrid}
            groupId="observations-timeseries"
          >
            {timeSeriesConfigurations.map((config, index) => (
              <SortableItem key={`${config.title}-${index}`} index={index}>
                <TimeSeriesConfigCard
                  config={config}
                  parameters={parameters ?? []}
                  onEdit={() => openFor(index)}
                  onRemove={() => setRemoveConfirmIndex(index)}
                />
              </SortableItem>
            ))}
          </SortableList>
        ) : (
          <div className={styles.emptyState}>
            <Icon name="chart-line" size={32} />
            <span className={styles.emptyStateText}>
              {t("admin.widgets.fields.observations_map.time_series.empty")}
            </span>
          </div>
        )}
      </div>
      <RemoveConfigConfirmDialog
        open={removeConfirmIndex !== null}
        onConfirm={() => {
          if (removeConfirmIndex !== null) {
            removeTimeSeriesConfig(removeConfirmIndex);
            setRemoveConfirmIndex(null);
          }
        }}
        onCancel={() => setRemoveConfirmIndex(null)}
      />
    </div>
  );
}
